import $ from 'jquery';

const NAME = 'toggleDelegate';
const DATA_KEY = `jax.${NAME}`;
const EVENT_KEY = `.${DATA_KEY}`;

const Event = {
  CHANGE: `change${EVENT_KEY}`,
};

const Selector = {
  // InputGroupAncestor is an element which contains all radios/checkboxes
  // of the same name, within the same form
  InputGroupAncestor: 'form',
  Inputs: 'input, select, textarea',
};

/**
 * This JS plugin extends the behavior of a Toggle-delegate to propogate the
 * `required` attribute of the original input to the delegated input.
 * If the delegate's input isn't required, nothing is done.
 *
 * A "toggle delegate" can be applied to an ancestor of a checkbox or radio input
 * so that a secondary input is revealed  when the toggle input is checked.
 *
 * The `Toggle-delegate` is a label which delegates the toggle input change to
 * a following `.Toggle-expand` with only CSS styling.
 */

class ToggleDelegate {
  /**
   * Instantiate on an existing element.
   *
   * @param {Element} element - the Toggle delegate label
   */

  constructor(element) {
    this.$element = $(element);

    // Select the delegate's input based on its `for` attribute
    const inputSelector = this.$element.attr('for');
    if (!inputSelector) {
      return;
    }

    this.$input = $(`#${inputSelector}`);

    // To see if the input is required, we need to check all elements in the
    // same radio/checkbox group (i.e., those having the same form name).
    // It may be required even if it doesn't itself have a required attr.
    const inputName = this.$input.attr('name');
    const $groupParent = this.$element.closest(Selector.InputGroupAncestor);
    const $inputGroup = $groupParent.find(`[name=${inputName}]`);
    if (!$inputGroup.is('[required]')) {
      return;
    }

    // The secondary input is the one whose visibility is toggled in response
    // to delegate changes. Because the .Toggle-expand must be a sibling of the
    // delegate, we can find the secondary input(s) through the parent.
    this.$secondaryInput = this.$element
      .parent()
      .find(Selector.Inputs)
      .not(this.$input);

    $inputGroup.on(Event.CHANGE, () => this.updateRequiredAttr());
    this.updateRequiredAttr();
  }

  /**
   * Add or remove the required attr from the secondary input
   * based on the checked state of the toggle delegate's input.
   */

  updateRequiredAttr() {
    if (this.$input.is(':checked')) {
      this.$secondaryInput.attr('required', '');
    } else {
      this.$secondaryInput.removeAttr('required');
    }
  }

  /**
   * Interface for using this class as a jQuery plugin.
   *
   * @returns {jQuery}
   */

  static _jQueryInterface() {
    return this.each(function () {
      let data = $(this).data(DATA_KEY);

      if (!data) {
        data = new ToggleDelegate(this);
        $(this).data(DATA_KEY, data);
      }
    });
  }
}

$.fn[NAME] = ToggleDelegate._jQueryInterface;
$.fn[NAME].Constructor = ToggleDelegate;

export default ToggleDelegate;
